import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { getPreviewUrl } from './preview-url';
import {
  PreviewMessageType,
  sendPreviewMessage,
  broadcastPropsUpdated,
  onBroadcastPropsUpdated,
} from './preview-messages';
import { useGenerateSignature } from '../hooks/useGenerateSignature';

// Iframe viewport size for a desktop view
const IFRAME_WIDTH = 1440;

export interface PreviewIframeHandle {
  notifyPropsUpdated: () => void;
  sendSectionData: (sectionId: string, data: Record<string, unknown>, sectionType?: string) => void;
}

interface PreviewIframeProps {
  pageId?: string;
  sectionId?: string;
  sectionType?: string;
  scrollToSectionId?: string;
  scaled?: boolean;
  websiteId?: string;
  autoScroll?: boolean;
  colorPreview?: string;
  logoPreview?: string;
  src?: string;
}

export const PreviewIframe = forwardRef<PreviewIframeHandle, PreviewIframeProps>(
  function PreviewIframe(
    {
      pageId,
      sectionId,
      sectionType,
      scrollToSectionId,
      scaled = true,
      websiteId: websiteIdProp,
      autoScroll: autoScrollProp,
      colorPreview,
      logoPreview,
      src: previewSrc,
    },
    ref
  ): ReactElement {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const iframeReady = useRef(false);
    const pendingSectionData = useRef<{ sectionId: string; sectionType?: string; data: Record<string, unknown> } | null>(null);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [searchParams] = useSearchParams();
    const plusWebsiteId = websiteIdProp ?? searchParams.get('websiteId') ?? '';
    const isPublicPreview = !!previewSrc;
    const src = previewSrc ?? getPreviewUrl(plusWebsiteId);
    const targetOrigin = src ? new URL(src).origin : '*';
    const { data: grant, isLoading, isError } = useGenerateSignature({ enabled: !isPublicPreview });

    useImperativeHandle(
      ref,
      () => ({
        notifyPropsUpdated: () => {
          sendPreviewMessage(
            iframeRef.current,
            { type: PreviewMessageType.PROPS_UPDATED },
            targetOrigin
          );
          broadcastPropsUpdated();
        },
        sendSectionData: (id: string, data: Record<string, unknown>, type?: string) => {
          const payload = { sectionId: id, sectionType: type, data };
          if (!iframeReady.current) {
            pendingSectionData.current = payload;
            return;
          }
          sendPreviewMessage(
            iframeRef.current,
            { type: PreviewMessageType.SECTION_DATA, payload },
            targetOrigin
          );
        },
      }),
      [targetOrigin]
    );

    const sendAuth = useCallback(() => {
      if (!grant) return;
      sendPreviewMessage(
        iframeRef.current,
        { type: PreviewMessageType.AUTH, payload: grant },
        targetOrigin
      );
    }, [grant, targetOrigin]);

    const sendPreview = useCallback(() => {
      sendPreviewMessage(
        iframeRef.current,
        {
          type: PreviewMessageType.PREVIEW,
          payload: {
            plusWebsiteId,
            pageId: pageId ?? '',
            sectionId,
            sectionType,
            scrollToSectionId,
          },
        },
        targetOrigin
      );
    }, [plusWebsiteId, pageId, sectionId, sectionType, scrollToSectionId, targetOrigin]);

    const sendColorPreviewMessage = useCallback(() => {
      if (!colorPreview) return;
      sendPreviewMessage(
        iframeRef.current,
        { type: PreviewMessageType.COLOR_PREVIEW, payload: { primaryColor: colorPreview } },
        targetOrigin
      );
    }, [colorPreview, targetOrigin]);

    const sendLogoPreviewMessage = useCallback(() => {
      if (!logoPreview) return;
      sendPreviewMessage(
        iframeRef.current,
        { type: PreviewMessageType.LOGO_PREVIEW, payload: { logoUrl: logoPreview } },
        targetOrigin
      );
    }, [logoPreview, targetOrigin]);

    const sendThemeOverride = useCallback(() => {
      if (!colorPreview && !logoPreview) return;
      sendPreviewMessage(
        iframeRef.current,
        {
          type: PreviewMessageType.THEME_OVERRIDE,
          payload: {
            primaryColor: colorPreview || undefined,
            logoUrl: logoPreview || undefined,
          },
        },
        targetOrigin
      );
    }, [colorPreview, logoPreview, targetOrigin]);

    useEffect(() => {
      const handler = (event: MessageEvent): void => {
        if (event.data?.type !== 'READY') return;
        if (event.source !== iframeRef.current?.contentWindow) return;
        const isLocalhost = event.origin.startsWith('http://localhost:');
        if (!isLocalhost && targetOrigin !== '*' && event.origin !== targetOrigin) return;

        iframeReady.current = true;
        setIframeLoaded(true);

        if (pendingSectionData.current) {
          sendPreviewMessage(
            iframeRef.current,
            { type: PreviewMessageType.SECTION_DATA, payload: pendingSectionData.current },
            targetOrigin
          );
          pendingSectionData.current = null;
        }

        // Public preview (home-preview) — only theme overrides, no auth or page context
        if (isPublicPreview) {
          sendThemeOverride();
          if (autoScrollProp) {
            sendPreviewMessage(
              iframeRef.current,
              { type: PreviewMessageType.AUTO_SCROLL },
              targetOrigin
            );
          }
          return;
        }

        if (grant) {
          sendAuth();
        }

        // Standalone color/logo demo (no page or section context)
        if (!pageId && !sectionId) {
          if (colorPreview) sendColorPreviewMessage();
          if (logoPreview) sendLogoPreviewMessage();
          return;
        }

        // Full page preview with optional theme overrides
        sendThemeOverride();
        sendPreview();
        if (autoScrollProp) {
          sendPreviewMessage(
            iframeRef.current,
            { type: PreviewMessageType.AUTO_SCROLL },
            targetOrigin
          );
        }
      };

      window.addEventListener('message', handler);
      return () => {
        window.removeEventListener('message', handler);
      };
    }, [
      grant,
      pageId,
      sendAuth,
      sendPreview,
      sendThemeOverride,
      sendColorPreviewMessage,
      sendLogoPreviewMessage,
      targetOrigin,
      autoScrollProp,
      colorPreview,
      logoPreview,
      isPublicPreview,
    ]);

    // Re-send theme overrides when color/logo change and iframe is ready
    useEffect(() => {
      if (!iframeReady.current) return;
      if (isPublicPreview || pageId || sectionId) {
        sendThemeOverride();
      } else {
        if (colorPreview) sendColorPreviewMessage();
        if (logoPreview) sendLogoPreviewMessage();
      }
    }, [
      isPublicPreview,
      pageId,
      sectionId,
      colorPreview,
      logoPreview,
      sendThemeOverride,
      sendColorPreviewMessage,
      sendLogoPreviewMessage,
    ]);

    // Re-send PREVIEW when the payload changes and iframe is already ready
    useEffect(() => {
      if (iframeReady.current && iframeRef.current) {
        sendPreview();
        if (autoScrollProp) {
          sendPreviewMessage(
            iframeRef.current,
            { type: PreviewMessageType.AUTO_SCROLL },
            targetOrigin
          );
        }
      }
    }, [sendPreview, autoScrollProp, targetOrigin]);

    // Re-send AUTH when grant refreshes (4-min token rotation)
    useEffect(() => {
      if (grant && iframeReady.current && iframeRef.current) {
        sendAuth();
      }
    }, [grant, sendAuth]);

    useEffect(() => {
      return onBroadcastPropsUpdated(() => {
        if (!iframeReady.current) return;
        sendPreviewMessage(
          iframeRef.current,
          { type: PreviewMessageType.PROPS_UPDATED },
          targetOrigin
        );
      });
    }, [targetOrigin]);

    // Scale iframe to fit container width
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    useEffect(() => {
      if (!scaled || !containerRef.current) return undefined;

      const observer = new ResizeObserver(([entry]) => {
        const { width } = entry.contentRect;
        if (width === 0) return;
        const s = width / IFRAME_WIDTH;
        setScale(s);
      });
      observer.observe(containerRef.current);

      return () => {
        observer.disconnect();
      };
    }, [scaled, isLoading]);

    if (!isPublicPreview && isLoading) {
      return (
        <div className="vtwo:w-full vtwo:h-full vtwo:flex vtwo:items-center vtwo:justify-center">
          <CircularProgress size={24} />
        </div>
      );
    }

    if (!src || (!isPublicPreview && isError)) {
      return (
        <div className="vtwo:w-full vtwo:h-full vtwo:flex vtwo:items-center vtwo:justify-center vtwo:text-gray-400">
          Preview not available
        </div>
      );
    }

    return (
      <div
        ref={containerRef}
        className="vtwo:relative vtwo:w-full vtwo:h-full vtwo:overflow-hidden"
      >
        {!iframeLoaded && (
          <div className="vtwo:absolute vtwo:inset-0 vtwo:z-10 vtwo:flex vtwo:items-center vtwo:justify-center vtwo:bg-stone-100">
            <CircularProgress size={24} />
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={src}
          title="Website preview"
          className={`vtwo:border-none ${
            scaled ? 'vtwo:origin-top-left' : 'vtwo:w-full vtwo:h-full'
          }`}
          sandbox="allow-scripts allow-same-origin"
          style={
            scaled && scale > 0
              ? {
                  width: `${IFRAME_WIDTH}px`,
                  height: `${100 / scale}%`,
                  transform: `scale(${scale})`,
                }
              : undefined
          }
        />
      </div>
    );
  }
);
